import * as CL from '@design-system/component-library';
import { useController, useFormContext } from 'react-hook-form';
import { eurosPerMonthMsg, priceMsg, t } from '../../../common/i18n/index.js';
import {
  getDeviceSelectionPrice,
  toggleAllDeviceIds,
  toggleDeviceId,
} from '../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import { formatSumToString } from '../../../common/utils/priceUtils.js';
import type { DeviceSelectionDeviceCoCo } from '../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';

import './DeviceSelectionDevicesTableCoCo.scss';

const EMPTY_SELECTION: string[] = [];

enum DeviceTableColumnCoCo {
  SELECT = 'select',
  NAME = 'name',
  PRICE = 'price',
}

interface ControlledCheckboxProps {
  name: string;
  checked: boolean;
  ariaLabel: string;
  onChange: () => void;
}

// CL.Checkbox keeps its own checked state, so the key re-mounts it when the controlled value changes
const ControlledCheckbox = ({ name, checked, ariaLabel, onChange }: ControlledCheckboxProps) => (
  <CL.Checkbox
    key={String(checked)}
    name={name}
    checked={checked}
    onChange={onChange}
    i18n_checkbox_ariaLabel={ariaLabel}
  />
);

// Opened in a new tab so that the unsaved device selection form is kept
const DeviceNameCell = ({ device }: { device: DeviceSelectionDeviceCoCo }) => (
  <div className="of-device-selection-devices-table-coco__device">
    <div className="of-device-selection-devices-table-coco__image">
      {device.imageUrl && <img src={device.imageUrl} alt="" loading="lazy" />}
    </div>
    {device.detailsPath ? (
      <a
        className="of-device-selection-devices-table-coco__link"
        href={device.detailsPath}
        target="_blank"
        rel="noopener noreferrer"
      >
        {device.name}
        <CL.Icon icon="external-link" type="regular" size="s" aria-hidden="true" />
      </a>
    ) : (
      <span>{device.name}</span>
    )}
  </div>
);

export interface DeviceSelectionDevicesTableCoCoProps {
  // Form field holding the ids of all selected devices, over all categories
  name: string;
  // Only visible devices are affected by select-all.
  devices: DeviceSelectionDeviceCoCo[];
}

export const DeviceSelectionDevicesTableCoCo = ({ name, devices }: DeviceSelectionDevicesTableCoCoProps) => {
  const { control } = useFormContext();
  const {
    field: { onChange, value },
  } = useController({ name, control, defaultValue: EMPTY_SELECTION });
  const selectedIds: string[] = value ?? EMPTY_SELECTION;
  const selectedIdSet = new Set(selectedIds);
  const allSelected = devices.length > 0 && devices.every(({ id }) => selectedIdSet.has(id));

  const columns = [
    {
      key: DeviceTableColumnCoCo.SELECT,
      label: (
        <ControlledCheckbox
          name={`${name}-select-all`}
          checked={allSelected}
          ariaLabel={t.SHYE('Select all devices')}
          onChange={() =>
            onChange(
              toggleAllDeviceIds(
                selectedIds,
                devices.map(({ id }) => id),
                allSelected
              )
            )
          }
        />
      ),
      width: '48px',
    },
    { key: DeviceTableColumnCoCo.NAME, label: t.X898('Device name'), width: '80%' },
    {
      key: DeviceTableColumnCoCo.PRICE,
      label:
        devices[0]?.oneTimePrice === undefined
          ? `${t.V72N(priceMsg)} ${t.H37G(eurosPerMonthMsg)}`
          : `${t.V72N(priceMsg)} €`,
      align: 'right' as const,
    },
  ];
  const rows = devices.map(device => ({
    rowid: device.id,
    [DeviceTableColumnCoCo.SELECT]: (
      <ControlledCheckbox
        name={`${name}-${device.id}`}
        checked={selectedIdSet.has(device.id)}
        ariaLabel={t.U7X7('Select {}', device.name)}
        onChange={() => onChange(toggleDeviceId(selectedIds, device.id))}
      />
    ),
    [DeviceTableColumnCoCo.NAME]: <DeviceNameCell device={device} />,
    [DeviceTableColumnCoCo.PRICE]: formatSumToString(getDeviceSelectionPrice(device), true),
  }));

  return (
    <div className="of-device-selection-devices-table-coco">
      <CL.Table columns={columns} rows={rows} tableType="bordered" responsive={false} valignText="middle" />
    </div>
  );
};
